/**
 * Geo-Tracking History Component
 * Display and manage user's location tracking history
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { geoTracking, GeoTrackingEvent } from '@/services/geoTracking';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Loader2,
  MapPin,
  Calendar,
  Filter,
  Download,
  Trash2,
  Eye,
  Navigation,
  Workflow,
  FileText,
  Globe,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { format } from 'date-fns';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export function GeoTrackingHistory() {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const { user } = useAuth();

  const [events, setEvents] = useState<GeoTrackingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [expandedEvents, setExpandedEvents] = useState<Set<number>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadHistory();
  }, [user, categoryFilter]);

  const loadHistory = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const filters = categoryFilter !== 'all' ? { eventCategory: categoryFilter } : {};
      const history = await geoTracking.getTrackingHistory(user.id, filters);
      setEvents(history);
    } catch (error) {
      console.error('Error loading tracking history:', error);
      toast.error('Failed to load tracking history');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!eventToDelete) return;

    setDeleting(true);
    try {
      const success = await geoTracking.deleteTrackingEvent(eventToDelete);
      if (success) {
        setEvents(events.filter((e) => e.id !== eventToDelete));
        toast.success('Event Deleted', {
          description: 'The tracking event has been removed from your history.',
        });
        setDeleteDialogOpen(false);
        setEventToDelete(null);
      } else {
        toast.error('Deletion Failed', {
          description: 'Could not delete the event. Please try again.',
        });
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Deletion Failed', {
        description: 'An error occurred while deleting the event.',
      });
    } finally {
      setDeleting(false);
    }
  };

  const toggleEventExpanded = (eventId: number) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }
    setExpandedEvents(newExpanded);
  };

  const getEventIcon = (category: string) => {
    switch (category) {
      case 'usage':
        return <Globe className="h-5 w-5 text-blue-500" />;
      case 'workflow':
        return <Workflow className="h-5 w-5 text-purple-500" />;
      case 'form':
        return <FileText className="h-5 w-5 text-green-500" />;
      case 'address':
        return <MapPin className="h-5 w-5 text-orange-500" />;
      default:
        return <Eye className="h-5 w-5 text-gray-500" />;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'usage':
        return 'Site Usage';
      case 'workflow':
        return 'Workflow';
      case 'form':
        return 'Form';
      case 'address':
        return 'Address';
      default:
        return category;
    }
  };

  const exportToCSV = () => {
    if (events.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = [
      'Date/Time',
      'Event Type',
      'Category',
      'Description',
      'Consent Verified',
      'Device Type',
      'Browser',
    ];

    const rows = events.map((event) => [
      fmt.custom(event.event_timestamp, 'yyyy-MM-dd HH:mm:ss'),
      event.event_type,
      event.event_category,
      event.event_description || '',
      event.consent_verified ? 'Yes' : 'No',
      event.device_type || '',
      event.browser || '',
    ]);

    const csvContent =
      [headers.join(','), ...rows.map((row) => row.map((cell) => `"${cell}"`).join(','))].join(
        '\n'
      );

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `geo-tracking-history-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast.success('Export Complete', {
      description: 'Your tracking history has been downloaded as a CSV file.',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Navigation className="h-6 w-6" />
            {t('settings.tracking_history', 'Tracking History')}
          </h2>
          <p className="text-muted-foreground mt-1">
            {t('settings.view_and_manage_your_location', 'View and manage your location tracking data')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportToCSV} disabled={events.length === 0}>
            <Download className="mr-2 h-4 w-4" />
            {t('settings.export_csv', 'Export CSV')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            {t('settings.filters', 'Filters')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="w-64">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger>
                  <SelectValue placeholder={t('settings.filter_by_category', 'Filter by category')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('settings.all_events', 'All Events')}</SelectItem>
                  <SelectItem value="usage">{t('settings.site_usage', 'Site Usage')}</SelectItem>
                  <SelectItem value="workflow">{t('settings.workflows', 'Workflows')}</SelectItem>
                  <SelectItem value="form">{t('settings.forms', 'Forms')}</SelectItem>
                  <SelectItem value="address">{t('settings.addresses', 'Addresses')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Events List */}
      <div className="space-y-3">
        {events.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Navigation className="h-12 w-12 text-gray-400 dark:text-gray-600 mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                {t('settings.no_tracking_events_found', 'No tracking events found')}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-1">
                {categoryFilter !== 'all'
                  ? 'Try changing the filter or enable tracking in your consent settings'
                  : 'Enable location tracking in your consent settings to start collecting data'}
              </p>
            </CardContent>
          </Card>
        ) : (
          events.map((event) => {
            const isExpanded = expandedEvents.has(event.id!);

            return (
              <Card key={event.id} className="overflow-hidden">
                <div
                  className="p-4 cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={() => toggleEventExpanded(event.id!)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="mt-1">{getEventIcon(event.event_category)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-sm font-medium">{event.event_type}</h4>
                          <span className="px-2 py-0.5 text-xs rounded-full bg-muted">
                            {getCategoryLabel(event.event_category)}
                          </span>
                          {event.consent_verified && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                              {t('settings.consented', 'Consented')}
                            </span>
                          )}
                        </div>
                        {event.event_description && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {event.event_description}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {fmt.custom(event.event_timestamp, 'MMM d, yyyy h:mm a')}
                          </span>
                          {event.device_type && (
                            <span>{event.device_type}</span>
                          )}
                          {event.duration_seconds && event.duration_seconds > 0 && (
                            <span>Duration: {Math.floor(event.duration_seconds / 60)}m {event.duration_seconds % 60}s</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEventToDelete(event.id!);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t bg-muted/30 p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground text-xs">{t('settings.session_id', 'Session ID')}</p>
                        <p className="font-mono text-xs">{event.session_id || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">{t('settings.consent_type', 'Consent Type')}</p>
                        <p className="text-xs">{event.consent_type || 'N/A'}</p>
                      </div>
                      {event.browser && (
                        <div>
                          <p className="text-muted-foreground text-xs">{t('settings.browser', 'Browser')}</p>
                          <p className="text-xs">{event.browser}</p>
                        </div>
                      )}
                      {event.os && (
                        <div>
                          <p className="text-muted-foreground text-xs">{t('settings.operating_system', 'Operating System')}</p>
                          <p className="text-xs">{event.os}</p>
                        </div>
                      )}
                    </div>

                    {event.event_metadata && Object.keys(event.event_metadata).length > 0 && (
                      <div>
                        <p className="text-muted-foreground text-xs mb-2">{t('settings.additional_data', 'Additional Data')}</p>
                        <pre className="text-xs bg-background p-2 rounded border overflow-x-auto">
                          {JSON.stringify(event.event_metadata, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('settings.delete_tracking_event', 'Delete Tracking Event?')}</DialogTitle>
            <DialogDescription>
              This will permanently delete this tracking event from your history. This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setEventToDelete(null);
              }}
              disabled={deleting}
            >
              {t('settings.cancel', 'Cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDeleteEvent} disabled={deleting}>
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('settings.deleting', 'Deleting...')}
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('settings.delete_event', 'Delete Event')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
